import {
  Scenario,
  SFFProblemType,
  Skill,
  RawPageData,
  PlayStats,
  ITSEvent,
  ITSEventType,
  TagResult,
  ITSEventData,
  BugData,
  TagText,
  PageType,
} from '../common/page-data';
import { getImageUrlFromScenario } from '../common/helpers';

export interface ITSEventLogger {
  (event: ITSEvent): void;
}

export interface PageDataParams {
  rawPageData: RawPageData;
  itsEventLogger?: ITSEventLogger;
}

export abstract class PageData {
  protected _userId = 'unknown';
  protected _sessionId = 'unknown';
  protected _level = 0;
  protected _lastEventTime: number;
  protected _cumulativeTime: number;
  protected _rawPageData: RawPageData;
  protected _question: string | TagText;
  protected _events: ITSEvent[];
  protected _ignoreList: string[];
  protected _progress: number;
  protected _eventLogger?: ITSEventLogger;

  public set progress(progress: number) {
    this._progress = progress;
  }

  public get ignoreList(): string[] {
    return this._ignoreList;
  }

  public get question(): string | TagText {
    return this._question;
  }

  /** ??? Confusing, this is to indicate multiChoice or not. Differs from type which is the category pool */
  protected _problemType: string;
  public get problemType(): string {
    return this._problemType;
  }
  protected _scenario?: Scenario;
  public get scenario(): Scenario | undefined {
    return this._scenario;
  }
  protected _seed: number;
  public get seed(): number {
    return this._seed;
  }
  protected _bugData?: BugData;
  public get bugData(): BugData | undefined {
    return this._bugData;
  }
  protected _type: SFFProblemType;
  public get type(): SFFProblemType {
    return this._type;
  }
  public set type(value: SFFProblemType) {
    this._type = value;
  }
  protected _skill: Skill;
  public get skill(): Skill {
    return this._skill;
  }
  protected _problemQuality: boolean;
  public get problemQuality(): boolean {
    return this._problemQuality;
  }
  public get image(): string | undefined {
    return getImageUrlFromScenario(this._scenario);
  }

  public reportBug(bugData: BugData): void {
    const rawPageData: RawPageData = { ...this.rawData, bugData };
    this.recordEvent(ITSEventType.bugReport, rawPageData);
  }

  /**
   * This will trigger a redux action for an event (use this event to log to fluentd or save to a database)
   * 
   * @param eventType 
   * @param eventData 
   */
  public recordEvent(eventType: ITSEventType, eventData?: ITSEventData): void {
    const currentTime = Date.now();
    const deltaTime = currentTime - this._lastEventTime;

    if (eventType === ITSEventType.problemSubmit) eventData = this.rawData;

    if (deltaTime < 300000)
      // if the time between events is greater than 5 minute, don't
      // count the time
      this._cumulativeTime += deltaTime;

    const itsEvent: ITSEvent = {
      eventType,
      eventData,
      clientTime: currentTime,
    };

    this._events.push(itsEvent);
    this._lastEventTime = currentTime;

    if (this._eventLogger) {
      this._eventLogger(itsEvent);
    }
  }
  abstract get rawData(): RawPageData;

  public get score(): number {
    return 1;
  }

  public get tagResults(): TagResult[] {
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public reset(): void {}

  public get playStats(): PlayStats {
    const score = this.score;
    const userId = this._userId;
    const sessionId = this._sessionId;
    const level = this._level;
    const progress = this._progress;
    const submitEvent = {
      eventType: ITSEventType.problemSubmit,
      clientTime: Date.now(),
    };
    const events = [...this._events, submitEvent];
    const cumulativeTime = this._cumulativeTime;
    const tagResults = this.tagResults;

    const playStats: PlayStats = {
      userId,
      sessionId,
      level,
      score,
      events,
      cumulativeTime,
      tagResults,
      progress,
    };

    return playStats;
  }

  // export interface
  protected constructor(params: PageDataParams) {
    const { rawPageData, itsEventLogger } = params;
    const {
      question,
      scenario,
      seed,
      type,
      skill,
      playStats,
      ignoreList,
      problemQuality,
      problemType,
      bugData,
    } = rawPageData;
    this._problemType = problemType as string;
    this._eventLogger = itsEventLogger;
    this._lastEventTime = Date.now();
    this._cumulativeTime = 0;
    this._rawPageData = rawPageData;
    this._question = question || '';
    this._scenario = scenario;
    this._seed = seed;
    this._type = type;
    this._skill = skill;
    this._events = [];
    this._ignoreList = ignoreList;
    this._progress = 0;
    this._problemQuality = problemQuality || false;
    this._bugData = bugData;
    if (playStats) {
      this._userId = playStats.userId;
      this._sessionId = playStats.sessionId;
      this._level = playStats.level;
      this._events = playStats.events;
      this._cumulativeTime = playStats.cumulativeTime;
      this._progress = playStats.progress || 0;
    }
  }
}

export class VestigialData extends PageData {
  public get pageType(): PageType {
    return PageType.Banner;
  }

  public get rawData(): RawPageData {
    return this._rawPageData as RawPageData;
  }

  public constructor(params: PageDataParams) {
    super(params);
  }
}
